import { Alert as UiAlert } from '@Pages/pageRuntime/components/ui/Alert';
import { AlertDescription } from '@Pages/pageRuntime/components/ui/AlertDescription';
import { AlertTitle } from '@Pages/pageRuntime/components/ui/AlertTitle';
import type React from 'react';

type AlertProps = {
  /**
   * Alert visual style.
   */
  variant?: 'default' | 'info' | 'warning' | 'success' | 'destructive';

  /**
   * Optional alert heading.
   */
  title?: React.ReactNode;

  /**
   * Primary alert body.
   */
  description?: React.ReactNode;

  /**
   * Alternate alert body content used when `description` is not provided.
   */
  children?: React.ReactNode;

  className?: string;
};

/**
 * A callout for contextual notices, success states, warnings, and errors. Use sparingly
 * for information that should stand out from the surrounding page.
 *
 * @example
 * <Sdk.Components.Alert variant="info" title="Heads up" description="Student data was updated recently." />
 *
 * @example
 * <Sdk.Components.Alert variant="success" title="Saved" description="The student record is up to date." />
 *
 * @example
 * <Sdk.Components.Alert variant="warning" title="Action needed">Confirm the student's contact details.</Sdk.Components.Alert>
 */
export function Alert({
  variant = 'default',
  title,
  description,
  children,
  className
}: AlertProps) {
  return (
    <UiAlert variant={variant} className={className}>
      {title ? <AlertTitle>{title}</AlertTitle> : null}
      {description || children ? (
        <AlertDescription>{description ?? children}</AlertDescription>
      ) : null}
    </UiAlert>
  );
}
